// Shared shift-timing validation — the frontend half of the single
// validation rule enforced everywhere a shift can be set (Organization
// Settings predefined shifts, and a job's custom shift on Create/Edit/
// Clone). Mirrors the backend's shared validator
// (server/app/api/v2/shift_timing/validators.py) so both layers agree on
// the exact same rule and the exact same messages.
import { z } from "zod";
import { parseTimeToMinutes } from "@/utils/shiftTime";

export const SHIFT_MESSAGES = {
  startRequired: "Start time is required.",
  endRequired: "End time is required.",
  timezoneRequired: "Time zone is required.",
  sameStartAndEnd: "Start time and end time must not be the same.",
};

/**
 * Validates a {shiftStartTime, shiftEndTime, shiftTimezone} triple (24-hour
 * display strings for the times, an IANA name for the timezone) and pushes
 * zod issues at the given paths. Used both standalone (predefined shift
 * modal) and embedded inside a larger schema's superRefine (job form,
 * gated on shiftType === "custom").
 *
 * Overnight shifts are valid — e.g. start "21:00", end "06:00" is a
 * normal night shift crossing midnight, not an error. The only rejected
 * time-range case is an identical start and end (ambiguous: zero-length,
 * or a full 24 hours — neither is a real shift).
 */
export function validateShiftTimeFields({ shiftStartTime, shiftEndTime, shiftTimezone }, ctx, { pathPrefix = [] } = {}) {
  const path = (field) => [...pathPrefix, field];

  if (!shiftStartTime) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: path("shiftStartTime"), message: SHIFT_MESSAGES.startRequired });
  }
  if (!shiftEndTime) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: path("shiftEndTime"), message: SHIFT_MESSAGES.endRequired });
  }
  if (!shiftTimezone) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: path("shiftTimezone"), message: SHIFT_MESSAGES.timezoneRequired });
  }

  const startMin = parseTimeToMinutes(shiftStartTime);
  const endMin = parseTimeToMinutes(shiftEndTime);
  if (startMin !== null && endMin !== null && startMin === endMin) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: path("shiftEndTime"), message: SHIFT_MESSAGES.sameStartAndEnd });
  }
}

/**
 * Called from jobFormSchema's top-level superRefine. Only "custom" needs
 * the full time/timezone check here — "predefined" just needs an id,
 * "none" (the user clearing the shift) needs nothing, and a falsy shiftType
 * (the picker never touched) is legacy behaviour and untouched.
 */
export function validateJobShiftSelection(data, ctx) {
  if (data.shiftType === "predefined") {
    if (!data.predefinedShiftId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["predefinedShiftId"],
        message: "Please select a predefined shift.",
      });
    }
    return;
  }
  if (data.shiftType === "custom") {
    validateShiftTimeFields(data, ctx);
  }
}

// Standalone schema for the Organization Settings Add/Edit Shift modal —
// every predefined shift always has all three fields, no conditional gate.
export const shiftTimingSchema = z
  .object({
    name: z.string().trim().min(1, "Shift name is required.").max(100, "Shift name must be 100 characters or fewer."),
    shiftStartTime: z.string().optional().or(z.literal("")),
    shiftEndTime: z.string().optional().or(z.literal("")),
    shiftTimezone: z.string().optional().or(z.literal("")),
  })
  .superRefine((data, ctx) => validateShiftTimeFields(data, ctx));
